import { type WithChildren } from "@common/declarations";
import classnames from "classnames";
import { isUndefined } from "lodash";
import * as React from "react";
import TabContentInner, { type TabContentProps } from "./TabContent/component";
import TabHeaderInner, { type TabHeaderProps } from "./TabHeader/component";

const TabsHeader = (props: WithChildren) => null;
const TabsContent = (props: WithChildren) => null;
const TabHeader = TabHeaderInner;
const TabContent = TabContentInner;

export { TabContent, TabHeader, TabsContent, TabsHeader };

export interface TabsProps {
  activeTabIndex?: number;
  className?: string;
  isNarrow?: boolean;
  onDemand?: boolean;
  onTabActivated?: (index: number) => void;
  children: Array<any>;
}

interface State {
  activeTabIndex: number;
}

export default class Tabs extends React.PureComponent<TabsProps, State> {
  constructor(props: TabsProps) {
    super(props);

    this.state = {
      activeTabIndex: this._getActiveTabIndexFromProps(props) ?? 0
    };
  }

  public componentDidUpdate(prevProps: Readonly<TabsProps>, prevState: Readonly<State>, snapshot?: any): void {
    if (prevProps.activeTabIndex !== this.props.activeTabIndex) {
      const activeTabIndex = this._getActiveTabIndexFromProps(this.props) ?? 0;
      if (this.state.activeTabIndex !== activeTabIndex) this.setState({ activeTabIndex });
    }
  }

  public render() {
    const { className, onDemand } = this.props;
    const { activeTabIndex } = this.state;

    const [th, tc] = this._getTHTC(this.props);
    return (
      <div className={classnames("tabs", className, { narrow: this.props.isNarrow })}>
        <ul className="tabs-navigation">
          {React.Children.toArray(th.props.children)
            .filter((c) => !!c)
            .map<React.ReactElement<TabHeaderProps>>((child: React.ReactElement<any>, index) =>
              React.cloneElement(child, {
                ...child.props,
                key: index,
                isActive: activeTabIndex === index,
                onClick: this._onTabClick(index, child.props.onClick)
              })
            )}
        </ul>
        <div className="tabs-content">
          {React.Children.toArray(tc.props.children)
            .filter((c) => !!c)
            .map((child: React.ReactElement<TabContentProps>, index) =>
              (child && !onDemand) || activeTabIndex === index
                ? React.cloneElement(child, {
                    ...child.props,
                    key: index,
                    isActive: activeTabIndex === index
                  })
                : null
            )}
        </div>
      </div>
    );
  }

  private _getActiveTabIndexFromProps = (props: TabsProps): number | undefined => {
    if (isUndefined(props.activeTabIndex)) return;

    const [th] = this._getTHTC(props);
    const tabsCount = React.Children.count(th.props.children);

    const index = props.activeTabIndex!;
    return Math.min(Math.max(index, 0), tabsCount - 1);
  };

  private _onTabClick(index: number, onClick?: () => void) {
    return () => {
      this.setState({ activeTabIndex: index });
      this.props.onTabActivated && this.props.onTabActivated(index);

      onClick && onClick();
    };
  }

  private _getTHTC = (props: TabsProps): [React.ReactElement<WithChildren>, React.ReactElement<WithChildren>] => {
    const rch = React.Children.toArray(props.children);

    const th = rch[0] as React.ReactElement<WithChildren>;
    if (!th) throw new Error("TabsHeader must be a direct first child");

    const tc = rch[1] as React.ReactElement<WithChildren>;
    if (!tc) throw new Error("TabsContent must be a direct second child");

    return [th, tc];
  };
}
